import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Block } from '@/types';
import styles from '../ShutdownRitual.module.css';

interface PlanTomorrowStepProps {
  blocks: Block[];
  onAddBlock: (block: Omit<Block, 'id' | 'userId' | 'date' | 'createdAt' | 'updatedAt'>) => void;
  onRemoveBlock: (blockId: string) => void;
  carryOverBlocks: Block[];
}

const CATEGORIES = [
  { id: 'deep-work', name: 'Deep Work', color: '#2b6cb0' },
  { id: 'shallow', name: 'Shallow', color: '#9f7aea' },
  { id: 'personal', name: 'Personal', color: '#38a169' },
  { id: 'meeting', name: 'Meeting', color: '#ed8936' },
];

function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hours, mins, 0, 0);
  return format(date, 'h:mm a');
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
};

export function PlanTomorrowStep({
  blocks,
  onAddBlock,
  onRemoveBlock,
  carryOverBlocks,
}: PlanTomorrowStepProps) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [startHour, setStartHour] = useState(9);
  const [duration, setDuration] = useState(60);
  const [categoryId, setCategoryId] = useState('deep-work');

  const selectedCategory = CATEGORIES.find((c) => c.id === categoryId) || CATEGORIES[0];

  const handleAddBlock = useCallback(() => {
    if (!title.trim()) return;

    onAddBlock({
      title: title.trim(),
      startTime: startHour * 60,
      duration,
      categoryId,
      color: selectedCategory.color,
      notes: '',
      status: 'planned',
      linkedAlternativeId: null,
    });

    // Reset form
    setTitle('');
    setStartHour(9);
    setDuration(60);
    setCategoryId('deep-work');
    setShowForm(false);
  }, [title, startHour, duration, categoryId, selectedCategory.color, onAddBlock]);

  const totalBlocks = blocks.length + carryOverBlocks.length;

  return (
    <motion.div
      className={styles.stepContent}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2 className={styles.stepTitle} variants={itemVariants}>
        Plan Tomorrow
      </motion.h2>
      <motion.p className={styles.stepSubtitle} variants={itemVariants}>
        Set yourself up for success by planning a few key blocks for tomorrow.
      </motion.p>

      {/* Carry Over Blocks */}
      {carryOverBlocks.length > 0 && (
        <motion.div className={styles.carryOverSection} variants={itemVariants}>
          <p className={styles.carryOverLabel}>Carried Over from Today</p>
          <div className={styles.blockList}>
            {carryOverBlocks.map((block) => (
              <div key={block.id} className={styles.blockItem}>
                <div
                  className={styles.blockColor}
                  style={{ backgroundColor: block.color }}
                  aria-hidden="true"
                />
                <div className={styles.blockInfo}>
                  <p className={styles.blockTitle}>{block.title}</p>
                  <p className={styles.blockTime}>
                    {formatTime(block.startTime)} - {formatTime(block.startTime + block.duration)}
                  </p>
                </div>
                <span
                  className={`${styles.blockStatus}`}
                  style={{ background: 'rgba(237, 137, 54, 0.2)', color: '#ed8936' }}
                >
                  Carried
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* New Blocks */}
      <motion.div className={styles.planSection} variants={itemVariants}>
        {blocks.length > 0 && <p className={styles.planLabel}>New Blocks for Tomorrow</p>}
        <div className={styles.blockList}>
          <AnimatePresence mode="popLayout">
            {blocks.map((block) => (
              <motion.div
                key={block.id}
                className={styles.blockItem}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0, x: -100 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                layout
              >
                <div
                  className={styles.blockColor}
                  style={{ backgroundColor: block.color }}
                  aria-hidden="true"
                />
                <div className={styles.blockInfo}>
                  <p className={styles.blockTitle}>{block.title}</p>
                  <p className={styles.blockTime}>
                    {formatTime(block.startTime)} - {formatTime(block.startTime + block.duration)}
                  </p>
                </div>
                <motion.button
                  className={styles.removeBtn}
                  onClick={() => onRemoveBlock(block.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={`Remove block: ${block.title}`}
                >
                  Remove
                </motion.button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Add Block Form */}
      <AnimatePresence>
        {showForm ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden', marginBottom: '16px' }}
          >
            <div style={{ padding: '20px', background: '#222', border: '1px dashed #444' }}>
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '11px',
                    color: '#888',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Title
                </label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="What will you work on?"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  marginBottom: '16px',
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '11px',
                      color: '#888',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Start Time
                  </label>
                  <select
                    value={startHour}
                    onChange={(e) => setStartHour(Number(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#222',
                      border: '2px dashed #444',
                      color: '#f5f5f0',
                      fontFamily: 'Courier New, monospace',
                      fontSize: '14px',
                    }}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {formatTime(i * 60)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '11px',
                      color: '#888',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Duration
                  </label>
                  <select
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#222',
                      border: '2px dashed #444',
                      color: '#f5f5f0',
                      fontFamily: 'Courier New, monospace',
                      fontSize: '14px',
                    }}
                  >
                    <option value={30}>30 min</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                    <option value={180}>3 hours</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '11px',
                    color: '#888',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Category
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategoryId(cat.id)}
                      style={{
                        padding: '8px 16px',
                        background: categoryId === cat.id ? cat.color : 'transparent',
                        border: `2px solid ${cat.color}`,
                        color: categoryId === cat.id ? '#fff' : cat.color,
                        fontFamily: 'Courier New, monospace',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: cat.color,
                        }}
                      />
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowForm(false)}
                  style={{
                    padding: '10px 24px',
                    background: 'transparent',
                    border: '2px solid #555',
                    color: '#888',
                    fontFamily: 'Courier New, monospace',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddBlock}
                  disabled={!title.trim()}
                  style={{
                    padding: '10px 24px',
                    background: title.trim() ? '#38a169' : '#333',
                    border: 'none',
                    color: '#fff',
                    fontFamily: 'Courier New, monospace',
                    fontSize: '13px',
                    cursor: title.trim() ? 'pointer' : 'not-allowed',
                    opacity: title.trim() ? 1 : 0.5,
                  }}
                >
                  Add Block
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.button
            className={styles.addBlockBtn}
            onClick={() => setShowForm(true)}
            variants={itemVariants}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            + Add a block for tomorrow
          </motion.button>
        )}
      </AnimatePresence>

      {/* Summary */}
      <motion.p
        style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '16px' }}
        variants={itemVariants}
      >
        {totalBlocks} block{totalBlocks !== 1 ? 's' : ''} planned for tomorrow
      </motion.p>
    </motion.div>
  );
}

export default PlanTomorrowStep;
